import { Permission } from '@act/shared/authorization';
import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import { formatPhoneNumber, StringHelper } from '@act/shared/utilities';
import { TaggableChip } from '@act/storybook';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { useAppNavigation } from 'react-components/app/routing/routes';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { selectUserOrMemberById } from 'redux/utils/selectors/utility-selectors';
import { useAppSelector } from 'redux/store-hooks';
import styled from 'styled-components';
import AudioPlayer from 'toolkit/media/audio-player';
import { timeFormatter } from '@act/shared/utilities';
import { InlineAnchor } from 'utilities/inline-anchor';

const HighlightableRow = styled(TableRow)`
  &:hover {
    background-color: rgba(25, 28, 29, 0.08);
    cursor: pointer;
  }
`;

interface CallRowProps {
  call: CallDto;
  actingUser: UserDto;
  openAdditionalMenu: (event, CallDto) => void;
}

const CallRow = ({ call, openAdditionalMenu, actingUser }: CallRowProps) => {
  const fromUserObject = useAppSelector(state =>
    selectUserOrMemberById(state, { id: call.originator?.userId })
  );
  const toUserObject = useAppSelector(state =>
    selectUserOrMemberById(state, { id: call.receiver?.userId })
  );

  // If call is assigned to a member, use subjectId to resolve member name
  // Always call the hook (Rules of Hooks) - pass null if no subjectId
  const assignedMember = membersAppSelectors.useSelectMemberById(
    call.subjectId || null
  );

  const isManager = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ASSIGN_CALL_REVIEWS
  );
  // Filter call review tasks for this specific call
  const voiceCallReview = call.voiceCallAudits?.find(
    task => Number(task.callId) === call.id && task.assigneeId === actingUser.id
  );

  // Get all audits for this specific call
  const callAudits =
    call.voiceCallAudits?.filter(task => Number(task.callId) === call.id) || [];
  // Check if all reviews are completed (only if there are audits for this call)
  // Since the backend filters out deleted audits, we only need to check completion status
  const allReviewsCompleted =
    callAudits.length > 0 &&
    callAudits.every(
      task => task.completedAt !== null || task.actionTaken === true
    );

  const { toMemberPage } = useAppNavigation();

  function determineCallStatusType(callStatus: string) {
    if (voiceCallReview) {
      if (
        (voiceCallReview.actionTaken === false &&
          voiceCallReview.completedAt === null) ||
        (!allReviewsCompleted && isManager)
      ) {
        return 'warning';
      }
      if (voiceCallReview.completedAt !== null || allReviewsCompleted) {
        return 'success';
      }
    }
    if (callStatus === 'ENDED') {
      return 'success';
    }
    if (callStatus === 'MISSED') {
      return 'warning';
    }
    if (callStatus === 'FAILED') {
      return 'error';
    }
    return 'neutral';
  }

  function determineCallStatusLabel(callStatus: string) {
    if (isManager && callAudits.length > 0 && !allReviewsCompleted) {
      return 'Reviews Assigned';
    }
    if (voiceCallReview) {
      if (!!call?.subjectId || call?.subjectIds?.length > 0) {
        return 'Call Reassigned';
      } else if (
        voiceCallReview.actionTaken === false &&
        voiceCallReview.completedAt === null
      ) {
        return 'Needs Review';
      } else if (
        (voiceCallReview.actionTaken === false &&
          voiceCallReview.completedAt !== null) ||
        allReviewsCompleted
      ) {
        return 'Reviewed';
      }
    }
    return StringHelper.sentenceCase(callStatus);
  }

  const goToSubject = call?.subjectId
    ? () => {
        toMemberPage(null, call.subjectId, {
          externalId: call.id,
          externalType: 'call',
        });
      }
    : null;

  const fromUserName =
    call.incoming && assignedMember?.name
      ? assignedMember.name
      : fromUserObject?.name;
  const toUserName =
    !call.incoming && assignedMember?.name
      ? assignedMember.name
      : toUserObject?.name;

  const fromUser = {
    name: fromUserName,
    action: call.incoming ? goToSubject : null,
  };

  if (!fromUserName) {
    fromUser.name = call.incoming ? 'Unknown' : 'Unknown (Reema)';
  }

  const toUser = {
    name: toUserName,
    action: !call.incoming ? goToSubject : null,
  };

  if (!toUserName) {
    toUser.name = call.incoming ? 'Unknown (Reema)' : 'Unknown';
  }

  return (
    <HighlightableRow
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      onClick={goToSubject ? () => goToSubject() : undefined}
    >
      <TableCell>
        <InlineAnchor onClick={fromUser.action}>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Typography variant="bodyMedium">{fromUser?.name}</Typography>
            {call.isFeedbackCall && (
              <FeedbackIcon color="primary" sx={{ marginLeft: '4px' }} />
            )}
          </Box>
        </InlineAnchor>
        {call?.originator?.phoneNumber && (
          <>
            <br />
            <Typography variant="bodyMedium">
              {formatPhoneNumber(call.originator.phoneNumber)}
            </Typography>
          </>
        )}
      </TableCell>
      <TableCell>
        <InlineAnchor onClick={toUser.action}>
          <Typography variant="bodyMedium">{toUser?.name}</Typography>
        </InlineAnchor>
        {call?.receiver?.phoneNumber && (
          <>
            <br />
            <Typography variant="bodyMedium">
              {formatPhoneNumber(call.receiver.phoneNumber)}
            </Typography>
          </>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="bodyMedium">
          {timeFormatter.epochToHumanReadableDate({
            date: call?.timeStarted,
            includeTime: true,
          })}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {call?.voicemailFileId && (
          <AudioPlayer
            fileId={call?.voicemailFileId}
            alwaysShowDownload={false}
          />
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="bodyLarge">
          {timeFormatter.millisToDurationString(call?.duration)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <TaggableChip
          color={determineCallStatusType(call?.status)}
          label={StringHelper.sentenceCase(call?.status)}
          isTag
        />
      </TableCell>
      {/**
       * If the call is incoming, we assign the `to` number,
       * otherwise we assign the `from` number;
       * */}
      <TableCell align="right">
        <IconButton
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            openAdditionalMenu(e, call);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </TableCell>
    </HighlightableRow>
  );
};

export default CallRow;
